import React, { Component } from "react";
import http from "../../modules/api";
import InstagramAutoSetupSaveProgressModal from "./InstagramAutoSetupSaveProgressModal";
import WarningDialog from "../../modules/dialogs/WarningDialog";
import ConfirmDialogV2 from "../../modules/dialogs/ConfirmDialogV2";

const AUTO_SETUP_ANALYSIS_RESULT_STORAGE_KEY =
  "instagram_auto_setup_analysis_result";
const AUTO_SETUP_PENDING_RESULT_ENTRY_KEY =
  "instagram_auto_setup_pending_result_entry";
const AUTO_SETUP_ANALYSIS_PROFILE_SEQ_STORAGE_KEY =
  "instagram_auto_setup_analysis_company_profile_seq";

const getStepFromLocation = () =>
  new URLSearchParams(window.location.search).get("step");

// 직접 URL 분석이 검증되지 않은 과거/실패 결과는 표시하지 않는다.
const isValidAutoSetupAnalysisResult = (value) => {
  if (!value || value.status !== "success" || !value.analysis?.source_analysis) {
    return false;
  }
  const inputType = String(value.input_type || value.source?.input_type || "").toUpperCase();
  return !["LINK", "COMPANY_URL"].includes(inputType) ||
    value.source?.url_analysis_status === "SUCCESS";
};

const emptyObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};

const toDialogContent = (message) =>
  String(message ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\r?\n/g, "<br/>");

const AUTO_SETUP_GPT_LIMIT_CODES = [
  "GPT_CALL_LIMIT", "GPT_WEB_SEARCH_CALL_LIMIT", "GPT_CALL_LIMIT_CHECK_ERROR",
  "GPT_WEB_SEARCH_LIMIT_CHECK_ERROR", "GPT_WEB_SEARCH_LIMIT_PERIOD_NOT_FOUND",
];

const getAutoSetupApiErrorMessage = (value, fallback) => {
  const data = value?.response?.data || value || {};
  const code = String(data.error_code || data.reason || "");
  const detail = data.message || data.error_msg;
  if (AUTO_SETUP_GPT_LIMIT_CODES.includes(code)) {
    return detail || "GPT 사용량을 확인할 수 없거나 사용 가능한 사용량이 부족합니다.";
  }
  return detail || fallback;
};

class InstagramAutoSetupResult extends Component {
  constructor(props) {
    super(props);

    // URL이 step=2일 때만 결과를 표시한다. 독립 Result 라우트도 새로고침 복원 허용.
    const isResultStep = getStepFromLocation() === "2";
    const receivedAnalysisResult = isResultStep
      ? props.analysisResult || this.loadStoredAnalysisResult()
      : null;
    const analysisResult = isValidAutoSetupAnalysisResult(receivedAnalysisResult)
      ? receivedAnalysisResult : null;

    this.analysisSaveChain = Promise.resolve();
    this.analysisSaveTimer = null;
    this.pendingAnalysisSave = null;
    this.state = {
      analysisResult,
      isImageGenerating: false,
      isRegenerating: false,
      regeneratingTarget: null,
      imageError: "",
      isEditing: true,
      isSaving: false,
      isManualSaving: false,
      manualSaveComplete: false,
      // 두 작업은 독립적으로 완료된다. 새로고침 후에도 중복 등록되지 않도록 분석 결과에 상태를 저장한다.
      isSaved: false, // 호환용 상태: 두 등록 버튼을 잠그지 않는다.
      isContentRegistered: false,
      isAccountLoading: false,
      instagramAccessTokenSeq:
        props.instagramAccessTokenSeq ||
        props.instagram_access_token_seq ||
        null,
      companyProfileSeq:
        props.companyProfileSeq ||
        props.company_profile_seq ||
        new URLSearchParams(window.location.search).get("company_profile_seq") ||
        null,
      selectedInstagramAccountName:
        props.instagramAccountName || "",
      saveProgressModalOpen: false,
      saveProgressStatus: "IDLE",
      saveProgressCurrentStepNo: 0,
      saveProgressCompletedStepNo: 0,
      saveProgressMessage: "",
      saveProgressError: "",
      saveProgressResult: null,
      showSuccessToast: false,
      warningDialogMessage: "",
      confirmReturnToStarterOpen: false,
      contentRegistrationModalOpen: false,
      contentRegistrationStatus: "IDLE",
      contentRegistrationCurrentStepNo: 0,
      contentRegistrationCompletedStepNo: 0,
      contentRegistrationMessage: "",
      contentRegistrationError: "",
      contentRegistrationResult: null,
    };
  }

  componentDidMount() {
    if (!this.state.analysisResult) {
      // step=2가 아니거나 복원할 정상 결과가 없으면 최초 입력 화면으로 이동한다.
      this.clearStoredAnalysisResult();
      const url = new URL(
        "/shop/instagram/InstagramAutoSetupStarter", window.location.origin
      );
      url.searchParams.set("step", "1");
      if (this.state.companyProfileSeq) {
        url.searchParams.set("company_profile_seq", String(this.state.companyProfileSeq));
      }
      window.location.replace(`${url.pathname}${url.search}`);
      return;
    }

    this.displaySuccessToast();
    this.loadDefaultInstagramAccount();
    this.ensurePreviewImage();
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.analysisResult &&
      this.props.analysisResult !== prevProps.analysisResult &&
      this.props.analysisResult !== this.state.analysisResult
    ) {
      if (!isValidAutoSetupAnalysisResult(this.props.analysisResult)) {
        this.setState({ analysisResult: null });
        return;
      }
      this.setState(
        {
          analysisResult: this.props.analysisResult,
          isSaved: false,
          isContentRegistered: false,
          saveProgressStatus: "IDLE",
          saveProgressCurrentStepNo: 0,
          saveProgressCompletedStepNo: 0,
          saveProgressMessage: "",
          saveProgressError: "",
          saveProgressResult: null,
        },
        this.ensurePreviewImage
      );
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.stopSaveProgressPolling();
    this.stopContentRegistrationPolling();
    if (this.analysisSaveTimer) window.clearTimeout(this.analysisSaveTimer);
    if (this.successToastTimer) {
      window.clearTimeout(this.successToastTimer);
    }
    if (this.manualSaveCompleteTimer) {
      window.clearTimeout(this.manualSaveCompleteTimer);
    }
  }

  clearStoredAnalysisResult = () => {
    try {
      sessionStorage.removeItem(AUTO_SETUP_ANALYSIS_RESULT_STORAGE_KEY);
      sessionStorage.removeItem(AUTO_SETUP_PENDING_RESULT_ENTRY_KEY);
      sessionStorage.removeItem(AUTO_SETUP_ANALYSIS_PROFILE_SEQ_STORAGE_KEY);
    } catch (error) {
      console.warn("[InstagramAutoSetupResult] 분석 결과 초기화 실패", error);
    }
  };

  loadStoredAnalysisResult = () => {
    try {
      const storedProfileSeq = sessionStorage.getItem(
        AUTO_SETUP_ANALYSIS_PROFILE_SEQ_STORAGE_KEY
      );
      const urlProfileSeq = new URLSearchParams(window.location.search).get(
        "company_profile_seq"
      );
      if (storedProfileSeq !== String(urlProfileSeq || "")) return null;
      const raw = sessionStorage.getItem(
        AUTO_SETUP_ANALYSIS_RESULT_STORAGE_KEY
      );
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.warn(
        "[InstagramAutoSetupResult] 분석 결과 불러오기 실패",
        error
      );
      return null;
    }
  };

  persistAnalysisResult = (analysisResult) => {
    try {
      sessionStorage.setItem(AUTO_SETUP_ANALYSIS_RESULT_STORAGE_KEY, JSON.stringify(analysisResult));
    } catch (error) {
      console.warn("[InstagramAutoSetupResult] 분석 결과 임시 저장 실패", error);
    }
    // 같은 분석 작업은 편집 중 UPDATE. 오래된 등록 상태는 기업 분석 JSON에 재기록하지 않는다.
    if (analysisResult?.auto_setup_analysis_seq) {
      this.pendingAnalysisSave = analysisResult;
      if (this.analysisSaveTimer) window.clearTimeout(this.analysisSaveTimer);
      this.analysisSaveTimer = window.setTimeout(() => {
        this.flushAnalysisSave().catch((error) => {
          console.error("[InstagramAutoSetupResult] 분석 저장 실패", error);
          if (!this.isUnmounted) this.showWarningDialog(error.message || "기업 분석 결과를 저장하지 못했습니다.");
        });
      }, 550);
    }
  };

  flushAnalysisSave = (override) => {
    if (this.analysisSaveTimer) window.clearTimeout(this.analysisSaveTimer);
    this.analysisSaveTimer = null;
    const current = override || this.pendingAnalysisSave || this.state.analysisResult;
    this.pendingAnalysisSave = null;
    if (!current?.auto_setup_analysis_seq) return this.analysisSaveChain;
    this.analysisSaveChain = this.analysisSaveChain.catch(() => {}).then(async () => {
      const response = await http.post("/rest/shop/instagram/auto-setup/analysis/save", {
        auto_setup_analysis_seq: current.auto_setup_analysis_seq,
        analysis_result: current,
      });
      const data = response?.data || {};
      if (data.status !== "success") throw new Error(data.message || "기업 분석 결과 저장 실패");
      return data;
    });
    return this.analysisSaveChain;
  };

  // 명시적 저장: 자동저장과 동일한 분석 결과 UPDATE API를 즉시 호출한다.
  // 자동화/콘텐츠 신규 등록용 complete API는 호출하지 않는다.
  handleManualSave = async () => {
    if (
      this.state.isManualSaving ||
      this.state.isSaving ||
      this.state.isRegenerating ||
      this.state.isImageGenerating
    ) return;

    const current = this.state.analysisResult;
    if (!current?.auto_setup_analysis_seq) {
      this.showWarningDialog("저장할 기업 분석 결과 번호를 확인할 수 없습니다.");
      return;
    }

    if (this.manualSaveCompleteTimer) {
      window.clearTimeout(this.manualSaveCompleteTimer);
      this.manualSaveCompleteTimer = null;
    }
    this.setState({ isManualSaving: true, manualSaveComplete: false });
    try {
      await this.flushAnalysisSave(current);
      if (this.isUnmounted || this.isReturningToStarter) return;
      this.setState({ manualSaveComplete: true });
      this.manualSaveCompleteTimer = window.setTimeout(() => {
        if (!this.isUnmounted) this.setState({ manualSaveComplete: false });
        this.manualSaveCompleteTimer = null;
      }, 2500);
    } catch (error) {
      console.error("[InstagramAutoSetupResult] 수동 저장 실패", error);
      if (!this.isUnmounted && !this.isReturningToStarter) {
        this.showWarningDialog(
          getAutoSetupApiErrorMessage(error, "기업 분석 결과를 저장하지 못했습니다.")
        );
      }
    } finally {
      if (!this.isUnmounted) this.setState({ isManualSaving: false });
    }
  };

  createSetupRequestId = () => {
    if (
      window.crypto &&
      typeof window.crypto.randomUUID === "function"
    ) {
      return window.crypto.randomUUID();
    }

    return `auto-setup-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 12)}`;
  };

  loadDefaultInstagramAccount = async () => {
    const propAccountSeq =
      this.props.instagramAccessTokenSeq ||
      this.props.instagram_access_token_seq;

    if (propAccountSeq) {
      this.setState({
        instagramAccessTokenSeq: propAccountSeq,
        selectedInstagramAccountName:
          this.props.instagramAccountName || "",
      });
      return;
    }

    this.setState({ isAccountLoading: true });

    try {
      const response = await http.get(
        "/rest/shop/instagram/profile/list"
      );
      const facebookAccounts = Array.isArray(response?.data?.list)
        ? response.data.list
        : [];
      const instagramAccounts = facebookAccounts.reduce(
        (accounts, facebookAccount) =>
          accounts.concat(
            Array.isArray(facebookAccount?.instagram_account_list)
              ? facebookAccount.instagram_account_list
              : []
          ),
        []
      );
      const selectedAccount =
        instagramAccounts.find(
          (account) => account?.default_yn === "Y"
        ) || instagramAccounts[0];

      this.setState({
        isAccountLoading: false,
        instagramAccessTokenSeq:
          selectedAccount?.instagram_access_token_seq || null,
        selectedInstagramAccountName:
          selectedAccount?.username || "",
      });
    } catch (error) {
      console.error(
        "[InstagramAutoSetupResult] Instagram 계정 조회 실패",
        error
      );
      this.setState({ isAccountLoading: false });
    }
  };

  stopSaveProgressPolling = () => {
    if (this.saveProgressPollingTimer) {
      window.clearInterval(this.saveProgressPollingTimer);
      this.saveProgressPollingTimer = null;
    }
    this.isSaveProgressPolling = false;
  };

  loadSaveProgress = async (setupRequestId) => {
    if (!setupRequestId || this.isSaveProgressPolling) return;

    this.isSaveProgressPolling = true;
    try {
      const response = await http.get(
        "/rest/shop/instagram/auto-setup/complete/progress",
        {
          params: { setup_request_id: setupRequestId },
        }
      );
      const data = response?.data || {};
      const progress = data.progress || {};
      const progressStatus = String(
        progress.progress_status || ""
      ).toUpperCase();

      if (!this.isUnmounted && data.status === "success") {
        this.setState({
          saveProgressStatus: progressStatus || "PROCESSING",
          saveProgressCurrentStepNo: Number(
            progress.current_step_no || 1
          ),
          saveProgressCompletedStepNo: Number(
            progress.completed_step_no || 0
          ),
          saveProgressMessage: progress.progress_message || "",
          saveProgressError: progress.error_message || "",
          saveProgressResult:
            progressStatus === "DONE"
              ? {
                  preset_seq: progress.preset_seq,
                  setting_seq: progress.setting_seq,
                  company_repository_seq:
                    progress.company_repository_seq,
                  publish_cycle: progress.publish_cycle,
                  scheduled_time: progress.scheduled_time,
                }
              : this.state.saveProgressResult,
        });
      }

      if (progressStatus === "DONE" || progressStatus === "FAILED") {
        this.stopSaveProgressPolling();
      }
    } catch (error) {
      // 저장 요청 직후 상태 행이 아직 생성되지 않은 경우 다음 polling에서 다시 확인한다.
      if (error?.response?.status !== 404) {
        console.warn(
          "[InstagramAutoSetupResult] 저장 진행 상태 조회 실패",
          error
        );
      }
    } finally {
      this.isSaveProgressPolling = false;
    }
  };

  startSaveProgressPolling = (setupRequestId) => {
    this.stopSaveProgressPolling();
    this.loadSaveProgress(setupRequestId);
    this.saveProgressPollingTimer = window.setInterval(() => {
      this.loadSaveProgress(setupRequestId);
    }, 400);
  };

  closeSaveProgressModal = () => {
    if (this.state.isSaving) return;
    this.pendingAutomationRequest = null;
    this.setState({ saveProgressModalOpen: false });
  };

  openNewTab = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  openSaveResultTarget = (callbackName, defaultUrl) => {
    const callback = this.props[callbackName];
    if (typeof callback === "function") {
      callback(this.state.saveProgressResult);
      return;
    }
    this.openNewTab(defaultUrl);
  };

  openCompanyRepository = () => {
    const result = this.state.saveProgressResult || {};
    const profileSeq =
      result.company_profile_seq || this.state.companyProfileSeq;
    const query = profileSeq
      ? `?company_profile_seq=${encodeURIComponent(profileSeq)}`
      : "";
    this.openSaveResultTarget(
      "onOpenCompanyRepository",
      `/shop/instagram/InstagramCompanyRepository${query}`
    );
  };

  openCopywriterSetting = () => {
    const result = this.state.saveProgressResult || {};
    const presetSeq = result.preset_seq;
    const query = presetSeq
      ? `?preset_seq=${encodeURIComponent(presetSeq)}`
      : "";
    this.openSaveResultTarget(
      "onOpenCopywriterSetting",
      `/shop/instagram/InstagramCopywriterSetting${query}`
    );
  };

  openAutomationSetting = () => {
    const result = this.state.saveProgressResult || {};
    const settingSeq = result.setting_seq;
    const query = settingSeq
      ? `?instagram_ai_image_auto_setting_seq=${encodeURIComponent(settingSeq)}`
      : "";
    this.openSaveResultTarget(
      "onOpenAutomationSetting",
      `/shop/instagram/InstagramCreateAiImageAutomation${query}`
    );
  };

  getMappedData = () => {
    const root = emptyObject(this.state.analysisResult);
    const source = emptyObject(root.source);
    const analysis = emptyObject(root.analysis);
    const sourceAnalysis = emptyObject(analysis.source_analysis);
    const recommended = emptyObject(
      analysis.recommended_settings
    );

    return {
      root,
      source,
      analysis,
      sourceAnalysis,
      copywriter: emptyObject(recommended.copywriter),
      content: emptyObject(recommended.content),
      aiStudio: emptyObject(recommended.ai_studio),
      automation: emptyObject(recommended.automation),
      previewImage: emptyObject(root.preview_image),
    };
  };

  // 재생성 시에는 현재 Result 왼쪽의 화면 설정값만 GPT에 제공한다.
  // 기존 분석의 상세 기업정보, 상품 목록, 저장된 브랜드 프로필은 제공하지 않는다.
  getResultRegenerateContext = () => {
    const { sourceAnalysis, copywriter } = this.getMappedData();
    return {
      industry: String(sourceAnalysis.industry || ""),
      copywriter: {
        target: String(copywriter.target || ""),
        language_code: String(copywriter.language_code || "ko"),
        objective: String(copywriter.objective || ""),
        extra_prompt: String(copywriter.extra_prompt || ""),
        call_to_action_position: String(copywriter.call_to_action_position || "bottom"),
        call_to_action_text: String(copywriter.call_to_action_text || ""),
      },
    };
  };

  // 기존 스튜디오 첨부 이미지는 새로 생성할 때에도 계속 전달한다.
  getStudioReferenceImageUrl = (source, previewImage) =>
    String(
      previewImage?.reference_image_url ||
      source.product_image_url ||
      source.company_image_url ||
      ""
    ).trim();

  getCompanyBrandContext = (source) => {
    const inputType = String(this.state.analysisResult?.input_type || source.input_type || "").toUpperCase();
    if (inputType !== "COMPANY_URL") return {};
    return {
      brand_color: source.brand_color || "",
      brand_color_list: Array.isArray(source.brand_color_list) ? source.brand_color_list : [],
      brand_color_source: source.brand_color_source || "",
      logo_image_url: source.logo_image_url || "",
    };
  };

  ensurePreviewImage = () => {
    const {
      root,
      source,
      sourceAnalysis,
      aiStudio,
      copywriter,
      content,
      previewImage,
    } = this.getMappedData();

    if (!root || !root.status) return;
    if (this.state.isImageGenerating) return;
    // 저장된 분석을 열 때 이미지가 없다면 사용자 요청 전까지 자동 생성하지 않는다.
    if (root.opened_from_history) return;

    if (
      previewImage.image_url &&
      previewImage.ai_studio_history_seq
    ) {
      return;
    }

    const imagePrompt = String(
      aiStudio.image_prompt || ""
    ).trim();

    if (!imagePrompt) {
      this.setState({
        imageError: "이미지 생성 프롬프트가 없습니다.",
      });
      return;
    }

    this.generatePreviewImage({
      image_prompt: imagePrompt,
      image_type_codes: Array.isArray(aiStudio.image_type_codes)
        ? aiStudio.image_type_codes
        : [],
      analysis_context: {
        source_analysis: sourceAnalysis,
        copywriter,
        content,
        ai_studio: aiStudio,
        company_brand_context: this.getCompanyBrandContext(source),
      },
      image_ratio: aiStudio.image_ratio || "1/1",
      history_title:
        copywriter.preset_title || "AI 자동 세팅 Preview",
      reference_image_url: String(
        source.product_image_url || source.company_image_url || ""
      ).trim(),
      product_link: String(
        source.resolved_product_link || source.product_link || source.resolved_company_url || source.company_url || ""
      ).trim(),
    });
  };

  generatePreviewImage = async (body) => {
    this.setState({
      isImageGenerating: true,
      imageError: "",
    });

    try {
      const data = await this.requestPreviewImage(body);
      if (this.isReturningToStarter || this.isUnmounted) return;

      const current = emptyObject(this.state.analysisResult);
      const next = {
        ...current,
        preview_image: data,
      };

      this.persistAnalysisResult(next);

      this.setState({
        analysisResult: next,
        isImageGenerating: false,
        imageError: "",
      });

      if (typeof this.props.onPreviewImageReady === "function") {
        this.props.onPreviewImageReady(data, next);
      }
    } catch (error) {
      if (this.isReturningToStarter || this.isUnmounted) return;
      this.setState({
        isImageGenerating: false,
        imageError:
          error?.response?.data?.message ||
          error?.message ||
          "AI 이미지 생성에 실패했습니다.",
      });
    }
  };

  requestPreviewImage = async (body) => {
    const response = await http.post(
      "/rest/shop/instagram/auto-setup/preview-image",
      body,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = response?.data || {};
    if (data.status !== "success") {
      throw new Error(
        getAutoSetupApiErrorMessage(data, "AI 이미지 생성에 실패했습니다.")
      );
    }
    return data;
  };

  requestRegeneratedAnalysis = async (
    regenerateScope,
    root,
    source,
    analysis
  ) => {
    const response = await http.post(
      "/rest/shop/instagram/auto-setup/regenerate",
      {
        regenerate_scope: regenerateScope,
        input_type: root.input_type || source.input_type || "",
        // 기업 분석 원본은 결과 보존을 위해 보내지만 GPT 입력은 result_context만 사용한다.
        result_context: this.getResultRegenerateContext(),
        source,
        analysis,
        parent_gpt_call_seq: root.gpt_call_seq || null,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = response?.data || {};
    if (data.status !== "success" || !data.analysis) {
      throw new Error(
        getAutoSetupApiErrorMessage(data, "AI 자동화 결과 재생성에 실패했습니다.")
      );
    }
    return data;
  };

  updateMappedField = (section, key, value) => {
    this.setState((prevState) => {
      const root = emptyObject(prevState.analysisResult);
      const analysis = emptyObject(root.analysis);
      const recommended = emptyObject(
        analysis.recommended_settings
      );
      const target = emptyObject(recommended[section]);

      const next = {
        ...root,
        analysis: {
          ...analysis,
          recommended_settings: {
            ...recommended,
            [section]: {
              ...target,
              [key]: value,
            },
          },
        },
      };

      this.persistAnalysisResult(next);
      return { analysisResult: next, manualSaveComplete: false };
    });
  };

  toggleEdit = () => {
    this.setState((prev) => ({
      isEditing: !prev.isEditing,
    }));
  };

  showWarningDialog = (message) => {
    this.setState({ warningDialogMessage: String(message ?? "") });
  };

  closeWarningDialog = () => {
    this.setState({ warningDialogMessage: "" });
  };

  handleReturnToStarter = () => {
    if (this.state.isSaving || this.state.isManualSaving) return;
    this.setState({ confirmReturnToStarterOpen: true });
  };

  confirmReturnToStarter = () => {
    if (this.state.isSaving || this.state.isManualSaving) return;
    this.setState({ confirmReturnToStarterOpen: false });

    // 이미 진행 중인 이미지/문구 생성 응답이 뒤늦게 도착하더라도 결과를 복원하지 않는다.
    this.isReturningToStarter = true;

    this.clearStoredAnalysisResult();
    if (typeof this.props.onReturnToStarter === "function") {
      this.props.onReturnToStarter();
      return;
    }

    // 독립 Result 라우트도 프로필 정보와 step=1을 유지하며 이동한다.
    const url = new URL(
      "/shop/instagram/InstagramAutoSetupStarter", window.location.origin
    );
    url.searchParams.set("step", "1");
    if (this.state.companyProfileSeq) {
      url.searchParams.set("company_profile_seq", String(this.state.companyProfileSeq));
    }
    window.location.href = `${url.pathname}${url.search}`;
  };

  handleRemake = async () => {
    if (
      this.state.isImageGenerating ||
      this.state.isRegenerating ||
      this.state.isSaving ||
      this.state.isManualSaving
    ) {
      return;
    }

    const { root, source, analysis } = this.getMappedData();

    this.setState({
      isRegenerating: true,
      isImageGenerating: true,
      regeneratingTarget: "ALL",
      imageError: "",
    });

    try {
      const regenerated = await this.requestRegeneratedAnalysis(
        "ALL",
        root,
        source,
        analysis
      );
      if (this.isReturningToStarter || this.isUnmounted) return;

      const nextSource = emptyObject(regenerated.source);
      const nextAnalysis = emptyObject(regenerated.analysis);
      const nextRecommended = emptyObject(
        nextAnalysis.recommended_settings
      );
      const nextCopywriter = emptyObject(
        nextRecommended.copywriter
      );
      const nextAiStudio = emptyObject(
        nextRecommended.ai_studio
      );
      const nextContent = emptyObject(
        nextRecommended.content
      );
      const imagePrompt = String(
        nextAiStudio.image_prompt || ""
      ).trim();

      if (!imagePrompt) {
        throw new Error("재생성된 이미지 프롬프트가 없습니다.");
      }

      const sourceForImage = Object.keys(nextSource).length
        ? nextSource
        : source;
      const previewImage = await this.requestPreviewImage({
        image_prompt: imagePrompt,
        image_type_codes: Array.isArray(nextAiStudio.image_type_codes)
          ? nextAiStudio.image_type_codes
          : [],
        regenerate_scope: "ALL",
        result_context: this.getResultRegenerateContext(),
        // 새로 생성된 문구만 이미지 기획에 연결하고 기존 기업 분석은 제외한다.
        analysis_context: {
          content: nextContent,
        },
        image_ratio: nextAiStudio.image_ratio || "1/1",
        history_title:
          nextCopywriter.preset_title ||
          "AI 자동 세팅 Preview",
        reference_image_url: this.getStudioReferenceImageUrl(
          sourceForImage,
          this.getMappedData().previewImage
        ),
        product_link: String(
          sourceForImage.resolved_product_link ||
            sourceForImage.product_link ||
            sourceForImage.resolved_company_url ||
            sourceForImage.company_url ||
            ""
        ).trim(),
      });
      if (this.isReturningToStarter || this.isUnmounted) return;

      // 문구 재생성과 이미지 생성이 모두 끝난 뒤 결과를 한 번에 교체한다.
      const next = {
        ...root,
        ...regenerated,
        source: sourceForImage,
        analysis: nextAnalysis,
        preview_image: previewImage,
      };

      this.persistAnalysisResult(next);
      this.setState({
        analysisResult: next,
        isRegenerating: false,
        isImageGenerating: false,
        regeneratingTarget: null,
        imageError: "",
      });

      if (typeof this.props.onPreviewImageReady === "function") {
        this.props.onPreviewImageReady(previewImage, next);
      }
      if (typeof this.props.onRegenerated === "function") {
        this.props.onRegenerated(next, regenerated, previewImage);
      }
    } catch (error) {
      if (this.isReturningToStarter || this.isUnmounted) return;
      const errorMessage = getAutoSetupApiErrorMessage(
        error, error?.message || "AI 자동화 결과 재생성에 실패했습니다.");
      this.setState({
        isRegenerating: false,
        isImageGenerating: false,
        regeneratingTarget: null,
        imageError: errorMessage,
      });
      this.showWarningDialog(errorMessage);
    }
  };

  handlePartialRemake = async (regenerateScope) => {
    if (
      this.state.isImageGenerating ||
      this.state.isRegenerating ||
      this.state.isSaving ||
      this.state.isManualSaving
    ) {
      return;
    }

    if (regenerateScope === "IMAGE") {
      await this.handleImageOnlyRemake();
      return;
    }

    const { root, source, analysis, previewImage } =
      this.getMappedData();

    this.setState({
      isRegenerating: true,
      regeneratingTarget: regenerateScope,
    });

    try {
      const regenerated = await this.requestRegeneratedAnalysis(
        regenerateScope,
        root,
        source,
        analysis
      );
      if (this.isReturningToStarter || this.isUnmounted) return;
      const nextSource = emptyObject(regenerated.source);
      const sourceToKeep = Object.keys(nextSource).length
        ? nextSource
        : source;
      const next = {
        ...root,
        ...regenerated,
        source: sourceToKeep,
        analysis: emptyObject(regenerated.analysis),
        preview_image: previewImage,
      };

      this.persistAnalysisResult(next);
      this.setState({
        analysisResult: next,
        isRegenerating: false,
        regeneratingTarget: null,
      });

      if (typeof this.props.onRegenerated === "function") {
        this.props.onRegenerated(next, regenerated, previewImage);
      }
    } catch (error) {
      if (this.isReturningToStarter || this.isUnmounted) return;
      const errorMessage = getAutoSetupApiErrorMessage(
        error, error?.message || "선택한 영역을 다시 만드는 데 실패했습니다.");
      this.setState({
        isRegenerating: false,
        regeneratingTarget: null,
      });
      this.showWarningDialog(errorMessage);
    }
  };

  handleImageOnlyRemake = async () => {
    if (this.state.isImageGenerating || this.state.isRegenerating || this.state.isSaving || this.state.isManualSaving) return;
    const { root, source, analysis } = this.getMappedData();
    this.setState({ isImageGenerating: true, regeneratingTarget: "IMAGE", imageError: "" });
    try {
      // 수정된 왼쪽 카피라이터 설정으로 자유형 이미지 기획을 GPT가 새로 만든다.
      const regenerated = await this.requestRegeneratedAnalysis("IMAGE", root, source, analysis);
      const nextAnalysis = emptyObject(regenerated.analysis);
      const nextRecommended = emptyObject(nextAnalysis.recommended_settings);
      const nextAiStudio = emptyObject(nextRecommended.ai_studio);
      const nextCopywriter = emptyObject(nextRecommended.copywriter);
      const nextSource = Object.keys(emptyObject(regenerated.source)).length
        ? regenerated.source : source;
      const imagePrompt = String(nextAiStudio.image_prompt || "").trim();
      if (!imagePrompt) throw new Error("새 이미지 기획 프롬프트가 없습니다.");
      // 기존 템플릿 평가(80점 기준), 적합시 템플릿용 GPT 호출,
      // 부적합시 방금 GPT가 만든 새 자유형 프롬프트를 사용한다.
      const previewImage = await this.requestPreviewImage({
        regenerate_scope: "IMAGE", // 템플릿 평가와 2차 GPT에도 이전 콘텐츠가 섞이지 않도록 지정
        image_prompt: imagePrompt,
        image_type_codes: Array.isArray(nextAiStudio.image_type_codes) ? nextAiStudio.image_type_codes : [],
        result_context: this.getResultRegenerateContext(),
        // IMAGE는 예전 피드 본문과 이미지 기획을 2차 GPT에 넘기지 않는다.
        analysis_context: {},
        image_ratio: nextAiStudio.image_ratio || "1/1",
        history_title: nextCopywriter.preset_title || "AI 자동 세팅 Preview",
        reference_image_url: this.getStudioReferenceImageUrl(
          nextSource,
          this.getMappedData().previewImage
        ),
        product_link: String(nextSource.resolved_product_link || nextSource.product_link || nextSource.resolved_company_url || nextSource.company_url || "").trim(),
      });
      if (this.isReturningToStarter || this.isUnmounted) return;
      const next = { ...root, source: nextSource, analysis: nextAnalysis, preview_image: previewImage };
      this.persistAnalysisResult(next);
      this.setState({ analysisResult: next, isImageGenerating: false, regeneratingTarget: null, imageError: "" });
      if (typeof this.props.onPreviewImageReady === "function") {
        this.props.onPreviewImageReady(previewImage, next);
      }
    } catch (error) {
      if (this.isReturningToStarter || this.isUnmounted) return;
      const message = getAutoSetupApiErrorMessage(error, error?.message || "이미지 재생성에 실패했습니다.");
      this.setState({ isImageGenerating: false, regeneratingTarget: null, imageError: message });
      this.showWarningDialog(message);
    }
  };

  displaySuccessToast = () => {
    if (this.successToastTimer) {
      window.clearTimeout(this.successToastTimer);
    }

    this.setState({ showSuccessToast: true });
    this.successToastTimer = window.setTimeout(() => {
      this.setState({ showSuccessToast: false });
    }, 3000);
  };

  handleFinalSave = async (retryMarker = null) => {
    const isCompanyProfileUpdate = false; // 자동화 버튼은 항상 신규 등록

    if (
      this.state.isRegenerating ||
      this.state.isImageGenerating ||
      this.state.isSaving ||
      this.state.isManualSaving
    ) {
      this.showWarningDialog("AI 자동화 결과 생성이 완료된 뒤 저장해주세요.");
      return;
    }

    if (!isCompanyProfileUpdate && this.state.imageError) {
      this.showWarningDialog("AI 이미지 생성 상태를 확인해주세요.");
      return;
    }

    if (!isCompanyProfileUpdate) {
      const { previewImage } = this.getMappedData();
      if (!previewImage.ai_studio_history_seq) {
        this.showWarningDialog("AI 스튜디오 히스토리가 아직 준비되지 않았습니다.");
        return;
      }
    }

    this.setState({ isSaving: true });

    try {
      // 팝업의 기술적 재시도에서는 같은 요청 ID와 당시 입력을 보존한다.
      // 사용자가 등록 버튼을 다시 클릭한 경우는 매번 새 요청 ID를 생성한다.
      const retry = retryMarker === "RETRY" && Boolean(this.pendingAutomationRequest);
      const setupRequestId = retry ? this.pendingAutomationRequest.requestId : this.createSetupRequestId();
      const analysisResult = retry
        ? this.pendingAutomationRequest.analysisResult
        : emptyObject(this.state.analysisResult);
      if (!retry) this.pendingAutomationRequest = { requestId: setupRequestId, analysisResult };
      await this.flushAnalysisSave(analysisResult);

      this.setState({
        saveProgressModalOpen: true,
        saveProgressStatus: "PROCESSING",
        saveProgressCurrentStepNo: 1,
        saveProgressCompletedStepNo: 0,
        saveProgressMessage: "새 AI 카피라이터 설정을 저장하고 있습니다.",
        saveProgressError: "",
        saveProgressResult: null,
      });
      this.startSaveProgressPolling(setupRequestId);

      const response = await http.post(
        "/rest/shop/instagram/auto-setup/complete",
        {
          company_profile_seq: this.state.companyProfileSeq || undefined,
          create_new_from_history: true,
          setup_request_id: setupRequestId,
          instagram_access_token_seq:
            this.state.instagramAccessTokenSeq,
          analysis_result: analysisResult,
          reuse_copywriter_preset_seq: null,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const data = response?.data || {};

      if (data.status !== "success") {
        const saveError = new Error(
          data.message ||
            (isCompanyProfileUpdate
              ? "기업정보 업데이트에 실패했습니다."
              : "자동화 설정 저장에 실패했습니다.")
        );
        saveError.responseData = data;
        throw saveError;
      }

      this.stopSaveProgressPolling();
      this.pendingAutomationRequest = null;
      // 등록 완료 후에도 새 자동화를 추가할 수 있다. 등록 요청 ID는 절대 분석 작업에 재사용하지 않는다.
      this.setState({
        isSaved: false,
        saveProgressModalOpen: true,
        saveProgressStatus: "DONE",
        saveProgressCurrentStepNo: 3,
        saveProgressCompletedStepNo: 3,
        saveProgressMessage:
          data.message ||
          (isCompanyProfileUpdate
            ? "기업정보가 업데이트되었습니다."
            : "자동화 설정이 저장되었습니다."),
        saveProgressError: "",
        saveProgressResult: data,
      });

      if (isCompanyProfileUpdate) {
        if (typeof this.props.onCompanyProfileUpdateComplete === "function") {
          this.props.onCompanyProfileUpdateComplete(data, analysisResult);
        }
      } else if (typeof this.props.onAutomationComplete === "function") {
        this.props.onAutomationComplete(data, analysisResult);
      }
    } catch (error) {
      this.stopSaveProgressPolling();
      const errorData =
        error?.response?.data || error?.responseData || {};
      this.setState({
        saveProgressModalOpen: true,
        saveProgressStatus: "FAILED",
        saveProgressCurrentStepNo: Number(
          errorData.failed_step_no ??
            (this.state.saveProgressCurrentStepNo || 1)
        ),
        saveProgressCompletedStepNo: Number(
          errorData.completed_step_no ??
            (this.state.saveProgressCompletedStepNo || 0)
        ),
        saveProgressError:
          errorData.message ||
          error?.message ||
          (isCompanyProfileUpdate
            ? "기업정보 업데이트에 실패했습니다."
            : "자동화 설정 저장에 실패했습니다."),
      });
    } finally {
      this.setState({ isSaving: false });
    }
  };

  stopContentRegistrationPolling = () => {
    if (this.contentRegistrationPollingTimer) {
      window.clearInterval(this.contentRegistrationPollingTimer);
      this.contentRegistrationPollingTimer = null;
    }
    this.isContentRegistrationPolling = false;
  };

  loadContentRegistrationProgress = async (setupRequestId) => {
    if (!setupRequestId || this.isContentRegistrationPolling) return;
    this.isContentRegistrationPolling = true;
    try {
      const response = await http.get("/rest/shop/instagram/auto-setup/complete/progress", {
        params: { setup_request_id: setupRequestId },
      });
      const progress = response?.data?.progress || {};
      const status = String(progress.progress_status || "").toUpperCase();
      if (!this.isUnmounted && response?.data?.status === "success") {
        this.setState({
          contentRegistrationStatus: status || "PROCESSING",
          contentRegistrationCurrentStepNo: Number(progress.current_step_no || 1),
          contentRegistrationCompletedStepNo: Number(progress.completed_step_no || 0),
          contentRegistrationMessage: progress.progress_message || "",
          contentRegistrationError: progress.error_message || "",
        });
      }
      if (status === "DONE" || status === "FAILED") this.stopContentRegistrationPolling();
    } catch (error) {
      if (error?.response?.status !== 404) {
        console.warn("[InstagramAutoSetupResult] 콘텐츠 등록 진행 상태 조회 실패", error);
      }
    } finally {
      this.isContentRegistrationPolling = false;
    }
  };

  startContentRegistrationPolling = (requestId) => {
    this.stopContentRegistrationPolling();
    this.loadContentRegistrationProgress(requestId);
    this.contentRegistrationPollingTimer = window.setInterval(() => {
      this.loadContentRegistrationProgress(requestId);
    }, 400);
  };

  closeContentRegistrationModal = () => {
    if (this.state.isSaving) return;
    this.pendingContentRequest = null;
    this.setState({ contentRegistrationModalOpen: false });
  };

  openRegisteredCopywriter = () => {
    const result = this.state.contentRegistrationResult || {};
    const presetSeq = result.preset_seq;
    const url = presetSeq
      ? `/shop/instagram/InstagramCopywriterSetting?preset_seq=${encodeURIComponent(presetSeq)}`
      : "/shop/instagram/InstagramCopywriterSetting";
    if (typeof this.props.onOpenCopywriterSetting === "function") {
      this.props.onOpenCopywriterSetting(result);
    } else {
      this.openNewTab(url);
    }
  };

  openRegisteredContent = () => {
    const result = this.state.contentRegistrationResult || {};
    if (!result.media_id) {
      this.showWarningDialog("등록된 콘텐츠 번호를 확인할 수 없습니다.");
      return;
    }
    const url = `/shop/instagram/InstagramCreatePost?media_id=${encodeURIComponent(result.media_id)}`;
    if (typeof this.props.onOpenRegisteredContent === "function") {
      this.props.onOpenRegisteredContent(result);
    } else {
      this.openNewTab(url);
    }
  };

  handleContentRegistration = async (retryMarker = null) => {
    if (this.state.isSaving || this.state.isManualSaving || this.state.isRegenerating || this.state.isImageGenerating) return;
    const { previewImage } = this.getMappedData();
    if (!previewImage.image_url || !previewImage.ai_studio_history_seq) {
      this.showWarningDialog("미리보기 이미지가 완성된 뒤 콘텐츠를 등록해주세요.");
      return;
    }

    const retry = retryMarker === "RETRY" && Boolean(this.pendingContentRequest);
    const current = retry
      ? this.pendingContentRequest.analysisResult : emptyObject(this.state.analysisResult);
    const requestId = retry ? this.pendingContentRequest.requestId : this.createSetupRequestId();
    if (!retry) this.pendingContentRequest = { requestId, analysisResult: current };
    this.setState({ isSaving: true });
    try {
      await this.flushAnalysisSave(current);
    } catch (error) {
      this.setState({ isSaving: false });
      this.showWarningDialog(error?.message || "기업 분석 수정 내용을 저장하지 못했습니다.");
      return;
    }

    this.setState({
      contentRegistrationModalOpen: true,
      contentRegistrationStatus: "PROCESSING",
      contentRegistrationCurrentStepNo: 1,
      contentRegistrationCompletedStepNo: 0,
      contentRegistrationMessage: "새 AI 카피라이터 설정을 저장하고 있습니다.",
      contentRegistrationError: "",
      contentRegistrationResult: null,
    });
    this.startContentRegistrationPolling(requestId);
    try {
      const response = await http.post(
        "/rest/shop/instagram/auto-setup/content/register-draft",
        {
          setup_request_id: requestId,
          analysis_result: current,
          reuse_copywriter_preset_seq: null,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      const data = response?.data || {};
      if (data.status !== "success") throw new Error(data.message || "콘텐츠 임시저장에 실패했습니다.");
      this.stopContentRegistrationPolling();
      this.pendingContentRequest = null;
      // 매 클릭 독립 INSERT. 완료 플래그를 분석 JSON에 보존하거나 버튼을 잠그지 않는다.
      this.setState({
        isContentRegistered: false,
        contentRegistrationStatus: "DONE",
        contentRegistrationCurrentStepNo: 2,
        contentRegistrationCompletedStepNo: 2,
        contentRegistrationMessage: data.message || "새 콘텐츠 임시저장이 완료되었습니다.",
        contentRegistrationError: "",
        contentRegistrationResult: data,
      });
      if (typeof this.props.onContentRegistrationComplete === "function") {
        this.props.onContentRegistrationComplete(data, current);
      }
    } catch (error) {
      this.stopContentRegistrationPolling();
      this.setState({
        contentRegistrationStatus: "FAILED",
        contentRegistrationError:
          error?.response?.data?.message || error?.message || "콘텐츠 임시저장에 실패했습니다.",
      });
    } finally {
      this.setState({ isSaving: false });
    }
  };

  getLanguageLabel = (languageCode) => {
    if (languageCode === "ko") return "한국어";
    if (languageCode === "en") return "영어";
    if (languageCode === "ja") return "일본어";
    if (languageCode === "zh") return "중국어";
    return languageCode || "";
  };

  getCtaPositionLabel = (position) => {
    if (position === "bottom") {
      return "문구 마지막 (하단)에 고정 추가";
    }
    if (position === "top") {
      return "문구 처음 (상단)에 고정 추가";
    }
    return position || "";
  };

  renderField = ({
    label,
    value,
    section,
    fieldKey,
    multiline = false,
    className = "",
  }) => {
    const editable =
      this.state.isEditing &&
      !this.state.isRegenerating &&
      section &&
      fieldKey;

    return (
      <div className={`auto-result-field ${className}`}>
        <label>{label}</label>
        {multiline ? (
          <textarea
            rows="4"
            readOnly={!editable}
            value={value || ""}
            onChange={(event) =>
              editable &&
              this.updateMappedField(
                section,
                fieldKey,
                event.target.value
              )
            }
          />
        ) : (
          <input
            type="text"
            readOnly={!editable}
            value={value || ""}
            onChange={(event) =>
              editable &&
              this.updateMappedField(
                section,
                fieldKey,
                event.target.value
              )
            }
          />
        )}
      </div>
    );
  };

  renderScopeRegenerateButton = (scope, label) => {
    const isLoading =
      this.state.regeneratingTarget === scope;
    const isBusy =
      this.state.isRegenerating ||
      this.state.isImageGenerating ||
      this.state.isSaving ||
      this.state.isManualSaving;

    return (
      <button
        type="button"
        className={`auto-result-scope-regenerate${
          isLoading ? " is-loading" : ""
        }`}
        title={label}
        aria-label={label}
        onClick={() => this.handlePartialRemake(scope)}
        disabled={isBusy}
      >
        <i className="fas fa-sync-alt" aria-hidden="true" />
      </button>
    );
  };

  renderImageArea = (previewImage) => {
    if (
      this.state.isRegenerating &&
      this.state.regeneratingTarget === "ALL"
    ) {
      return (
        <div className="auto-result-image-state">
          <div className="auto-result-spinner" />
          <strong>AI 자동화 결과 다시 만드는 중...</strong>
          <span>수정한 설정으로 문구와 이미지를 새로 만들고 있습니다.</span>
        </div>
      );
    }

    if (this.state.isImageGenerating) {
      return (
        <div className="auto-result-image-state">
          <div className="auto-result-spinner" />
          <strong>
            {this.state.regeneratingTarget === "IMAGE"
              ? "이미지만 다시 만드는 중..."
              : "AI 이미지 생성 중..."}
          </strong>
          <span>분석된 브랜드 방향으로 피드 이미지를 만들고 있습니다.</span>
        </div>
      );
    }

    if (previewImage.image_url) {
      return (
        <img
          className="auto-result-feed-image"
          src={previewImage.image_url}
          alt="AI 스튜디오 추천 이미지 Preview"
        />
      );
    }

    return (
      <div className="auto-result-image-state error">
        <strong>이미지를 표시하지 못했습니다.</strong>
        <span>{this.state.imageError}</span>
      </div>
    );
  };

  render() {
    if (!this.state.analysisResult) return null;

    const {
      root,
      sourceAnalysis,
      copywriter,
      content,
      aiStudio,
      automation,
      previewImage,
    } = this.getMappedData();

    if (!root || !root.status) {
      return (
        <div className="InstagramAutoSetupResult empty">
          분석 결과를 찾을 수 없습니다.
        </div>
      );
    }

    const hashtags = Array.isArray(content.preview_hashtags)
      ? content.preview_hashtags.join(" ")
      : "";

    // 미리보기에는 분석된 브랜드명만 사용한다. Instagram 계정명으로 대체하지 않는다.
    const displayName =
      String(sourceAnalysis.brand_name || "").trim() || "브랜드명 미확인";

    const avatarText = String(displayName)
      .replace(/^@/, "")
      .slice(0, 2)
      .toUpperCase();

    const templateName =
      aiStudio.template_name || "AI 직접 생성";

    return (
      <div className="InstagramAutoSetupResult">
        <style>{`
          .InstagramAutoSetupResult {
            --result-navy: #0a1a2f;
            --result-text: #263746;
            --result-muted: #6b7b88;
            --result-border: #dceaf0;
            --result-card: #ffffff;
            --result-soft: #f6fbfd;
            --result-mint: #00cdd2;
            --result-indigo: #5c68e8;
            --header-height: 72px;
            min-height: 100%;
            padding: 30px 28px 0;
            box-sizing: border-box;
            font-family: 'Pretendard', 'Noto Sans KR', sans-serif;
            color: var(--result-text);
            background: linear-gradient(180deg, #eefbff 0, #f8fdff 250px, #fff 620px);

            flex-shrink: 0;
            box-sizing: border-box;
          }
          @media (max-width: 767px) {
            .InstagramAutoSetupResult {
              --header-height: 60px;
            }
          }
          .InstagramAutoSetupResult * { box-sizing: border-box; }
          .InstagramAutoSetupResult .auto-result-success-toast {
            position: fixed;
            top: calc(var(--header-height) + 12px);
            left: 50%;
            z-index: 1000;
            transform: translateX(-50%);
            min-width: 360px;
            max-width: calc(100vw - 32px);
            padding: 15px 24px;
            border-radius: 12px 12px 12px 12px;
            background: #18742d;
            color: #fff;
            box-shadow: 0 12px 28px rgba(20, 82, 40, .24);
            font-size: 16px;
            font-weight: 800;
            text-align: center;
            animation: autoResultToastIn .2s ease-out;
            z-index: 100000;
          }
          @keyframes autoResultToastIn {
            from { opacity: 0; transform: translate(-50%, -8px); }
            to { opacity: 1; transform: translate(-50%, 0); }
          }
          .InstagramAutoSetupResult .font-bold { font-weight: 700; }
          .InstagramAutoSetupResult .auto-result-wrap {
            width: 100%;
            max-width: 1120px;
            margin: 0 auto 30px auto;
          }
          .InstagramAutoSetupResult .auto-result-page-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 12px;
          }
          .InstagramAutoSetupResult .auto-result-page-title {
            margin: 0;
            color: var(--result-navy);
            font-size: 16px;
            line-height: 1.5;
            font-weight: 800;
          }
          .InstagramAutoSetupResult .auto-result-header-actions {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            flex-wrap: wrap;
            gap: 8px;
          }
          .InstagramAutoSetupResult .auto-result-manual-save-button {
            padding: 9px 15px;
            border: 1px solid var(--result-mint);
            border-radius: 9px;
            background: var(--result-mint);
            color: var(--result-navy);
            font-size: 12px;
            font-weight: 800;
            cursor: pointer;
            min-width: 76px;
            transition: background .15s ease, border-color .15s ease;
          }
          .InstagramAutoSetupResult .auto-result-manual-save-button:hover:not(:disabled) {
            background: #00b9c0;
            border-color: #00b9c0;
          }
          .InstagramAutoSetupResult .auto-result-manual-save-button:disabled {
            opacity: .55;
            cursor: not-allowed;
          }
          .InstagramAutoSetupResult .auto-result-return-button {
            padding: 9px 13px;
            border: 1px solid #cfdde3;
            border-radius: 9px;
            background: #fff;
            color: var(--result-navy);
            font-size: 12px;
            font-weight: 750;
            cursor: pointer;
          }
          .InstagramAutoSetupResult .auto-result-return-button:hover {
            border-color: #98dfe2;
            background: #f1fcfc;
          }
          .InstagramAutoSetupResult .auto-result-return-button:disabled {
            opacity: .55;
            cursor: not-allowed;
          }
          .InstagramAutoSetupResult .auto-result-status-line {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 12px;
          }
          .InstagramAutoSetupResult .auto-result-status-button {
            border: 0;
            border-radius: 10px;
            padding: 10px 16px;
            background: var(--result-indigo);
            color: white;
            font-size: 13px;
            font-weight: 750;
          }
          .InstagramAutoSetupResult .auto-result-banner {
            display: none;
            align-items: center;
            justify-content: space-between;
            gap: 18px;
            padding: 22px;
            border: 1px solid #cfe5ec;
            border-radius: 18px;
            background: rgba(255,255,255,.92);
            box-shadow: 0 12px 32px rgba(29, 72, 91, .07);
          }
          .InstagramAutoSetupResult .auto-result-banner-main {
            display: flex;
            align-items: center;
            gap: 14px;
            min-width: 0;
          }
          .InstagramAutoSetupResult .auto-result-banner-icon {
            width: 46px;
            height: 46px;
            flex: 0 0 46px;
            border-radius: 13px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #eff2ff;
            border: 1px solid #d9defe;
            color: var(--result-indigo);
            font-size: 22px;
            padding: 8px;
          }
          .InstagramAutoSetupResult .auto-result-title-row {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 8px;
          }
          .InstagramAutoSetupResult .auto-result-title-row h2 {
            margin: 0;
            color: var(--result-navy);
            font-size: 20px;
            line-height: 1.42;
          }
          .InstagramAutoSetupResult .auto-result-mapped {
            padding: 3px 8px;
            border-radius: 999px;
            background: #e9fbf4;
            color: #178559;
            border: 1px solid #c7f1df;
            font-size: 10px;
            font-weight: 800;
          }
          .InstagramAutoSetupResult .auto-result-banner p {
            margin: 5px 0 0;
            color: var(--result-muted);
            font-size: 13px;
            line-height: 1.6;
          }
          .InstagramAutoSetupResult .auto-result-outline-button,
          .InstagramAutoSetupResult .auto-result-bottom-button {
            border: 1px solid #cfdde3;
            background: #fff;
            color: var(--result-navy);
            border-radius: 11px;
            font-size: 12px;
            font-weight: 750;
            cursor: pointer;
            transition: .15s ease;
          }
          .InstagramAutoSetupResult .auto-result-outline-button {
            padding: 11px 16px;
            white-space: nowrap;
          }
          .InstagramAutoSetupResult .auto-result-outline-button:disabled {
            opacity: .5;
            cursor: not-allowed;
          }
          .InstagramAutoSetupResult .auto-result-outline-button.active:not(:disabled),
          .InstagramAutoSetupResult .auto-result-outline-button:hover:not(:disabled),
          .InstagramAutoSetupResult .auto-result-bottom-button:hover {
            border-color: #98dfe2;
            background: #f1fcfc;
          }
          .InstagramAutoSetupResult .auto-result-grid {
            display: grid;
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 24px;
            margin-top: 24px;
          }
          .InstagramAutoSetupResult .auto-result-card {
            border: 1px solid var(--result-border);
            border-radius: 18px;
            background: var(--result-card);
            box-shadow: 0 10px 28px rgba(31, 68, 84, .065);
          }
          .InstagramAutoSetupResult .auto-result-settings {
            padding: 22px;
          }
          .InstagramAutoSetupResult .auto-result-card-heading {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            padding-bottom: 14px;
            margin-bottom: 16px;
            border-bottom: 1px solid #e8f0f3;
          }
          .InstagramAutoSetupResult .auto-result-card-heading h3 {
            margin: 0;
            font-size: 15px;
            color: var(--result-navy);
          }
          .InstagramAutoSetupResult .auto-result-setting-badge,
          .InstagramAutoSetupResult .auto-result-template-badge {
            padding: 5px 8px;
            border-radius: 8px;
            border: 1px solid #dbe0fd;
            background: #f3f5ff;
            color: #5260d3;
            font-size: 10px;
            font-weight: 750;
          }
          .InstagramAutoSetupResult .auto-result-fields {
            display: flex;
            flex-direction: column;
            gap: 14px;
          }
          .InstagramAutoSetupResult .auto-result-field-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
          }
          .InstagramAutoSetupResult .auto-result-field-grid.cta {
            grid-template-columns: .9fr 1.6fr;
          }
          .InstagramAutoSetupResult .auto-result-field label {
            display: block;
            margin-bottom: 6px;
            color: #657784;
            font-size: 11px;
            font-weight: 750;
          }
          .InstagramAutoSetupResult .auto-result-field input,
          .InstagramAutoSetupResult .auto-result-field textarea {
            width: 100%;
            border: 1px solid #dce7eb;
            border-radius: 11px;
            background: #f8fbfc;
            color: #334654;
            padding: 10px 11px;
            font: inherit;
            font-size: 12px;
            line-height: 1.55;
            outline: none;
          }
          .InstagramAutoSetupResult .auto-result-field textarea {
            min-height: 96px;
            resize: vertical;
          }
          .InstagramAutoSetupResult .auto-result-field input:not([readonly]),
          .InstagramAutoSetupResult .auto-result-field textarea:not([readonly]) {
            background: #fff;
            border-color: #8fdde0;
            box-shadow: 0 0 0 3px rgba(0,205,210,.08);
          }
          .InstagramAutoSetupResult .auto-result-preview-card {
            padding: 22px;
            display: flex;
            min-height: 100%;
            flex-direction: column;
          }
          .InstagramAutoSetupResult .auto-result-preview-desc {
            margin: 4px 0 0;
            color: var(--result-muted);
            font-size: 11px;
            line-height: 1.5;
          }
          .InstagramAutoSetupResult .auto-result-feed {
            border: 1px solid #dfe9ed;
            border-radius: 16px;
            overflow: hidden;
            background: #fff;
            box-shadow: 0 12px 28px rgba(27, 61, 76, .07);
            margin-bottom: 16px;
          }
          .InstagramAutoSetupResult .auto-result-feed-head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 11px 13px;
            border-bottom: 1px solid #edf2f4;
          }
          .InstagramAutoSetupResult .auto-result-account {
            display: flex;
            align-items: center;
            gap: 9px;
            min-width: 0;
          }
          .InstagramAutoSetupResult .auto-result-avatar {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 10px;
            font-weight: 800;
            background: linear-gradient(135deg, #00cdd2, #6673ec);
          }
          .InstagramAutoSetupResult .auto-result-account strong {
            display: block;
            max-width: 290px;
            color: var(--result-navy);
            font-size: 11px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .InstagramAutoSetupResult .auto-result-account small {
            display: block;
            margin-top: 2px;
            color: #83929c;
            font-size: 9px;
          }
          .InstagramAutoSetupResult .auto-result-feed-more {
            color: #8797a1;
            font-weight: 800;
            letter-spacing: 2px;
          }
          .InstagramAutoSetupResult .auto-result-image-wrap {
            position: relative;
            aspect-ratio: 1 / 1;
            background: #eef5f6;
            border-bottom: 1px solid #edf2f4;
          }
          .InstagramAutoSetupResult .auto-result-regenerate-scope {
            position: relative;
          }
          .InstagramAutoSetupResult .auto-result-scope-regenerate {
            position: absolute;
            top: 10px;
            right: 10px;
            z-index: 6;
            width: 32px;
            height: 32px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0;
            border: 1px solid rgba(73, 96, 111, .16);
            border-radius: 50%;
            color: #41566a;
            background: rgba(255, 255, 255, .92);
            box-shadow: 0 5px 14px rgba(26, 55, 72, .14);
            cursor: pointer;
            opacity: .25;
            transition:
              opacity .16s ease,
              color .16s ease,
              transform .16s ease,
              box-shadow .16s ease;
          }
          .InstagramAutoSetupResult .auto-result-regenerate-scope:hover >
          .auto-result-scope-regenerate,
          .InstagramAutoSetupResult .auto-result-scope-regenerate:focus-visible,
          .InstagramAutoSetupResult .auto-result-scope-regenerate.is-loading {
            opacity: 1;
          }
          .InstagramAutoSetupResult .auto-result-scope-regenerate:hover:not(:disabled) {
            color: var(--result-indigo);
            transform: translateY(-1px);
            box-shadow: 0 7px 18px rgba(62, 79, 172, .2);
          }
          .InstagramAutoSetupResult .auto-result-scope-regenerate:focus-visible {
            outline: 3px solid rgba(92, 104, 232, .24);
            outline-offset: 2px;
          }
          .InstagramAutoSetupResult .auto-result-scope-regenerate:disabled {
            cursor: not-allowed;
          }
          .InstagramAutoSetupResult .auto-result-scope-regenerate.is-loading i {
            animation: autoResultSpin .8s linear infinite;
          }
          .InstagramAutoSetupResult .auto-result-feed-image {
            width: 100%;
            height: 100%;
            display: block;
            object-fit: cover;
          }
          .InstagramAutoSetupResult .auto-result-image-state {
            width: 100%;
            height: 100%;
            min-height: 330px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 9px;
            padding: 28px;
            text-align: center;
            color: #566b79;
            background: linear-gradient(135deg, #f0fbfb, #f4f5ff);
          }
          .InstagramAutoSetupResult .auto-result-image-state strong {
            color: var(--result-navy);
            font-size: 14px;
          }
          .InstagramAutoSetupResult .auto-result-image-state span {
            max-width: 320px;
            font-size: 11px;
            line-height: 1.6;
          }
          .InstagramAutoSetupResult .auto-result-image-state.error {
            background: #fff8f7;
            color: #a85e55;
          }
          .InstagramAutoSetupResult .auto-result-spinner {
            width: 28px;
            height: 28px;
            border-radius: 50%;
            border: 3px solid #d9e9ec;
            border-top-color: var(--result-indigo);
            animation: autoResultSpin .8s linear infinite;
          }
          @keyframes autoResultSpin { to { transform: rotate(360deg); } }
          .InstagramAutoSetupResult .auto-result-feed-copy {
            padding: 14px;
            color: #405461;
            font-size: 11px;
            line-height: 1.65;
          }
          .InstagramAutoSetupResult .auto-result-caption-section,
          .InstagramAutoSetupResult .auto-result-hashtag-section {
            min-height: 34px;
            padding-right: 38px;
          }
          .InstagramAutoSetupResult .auto-result-caption-section {
            margin-bottom: 8px;
          }
          /* 미리보기 피드 본문은 GPT가 작성한 줄바꿈과 빈 줄을 그대로 표시한다. */
          .InstagramAutoSetupResult .auto-result-caption-text {
            white-space: pre-wrap;
            overflow-wrap: anywhere;
          }
          .InstagramAutoSetupResult .auto-result-caption-section
          .auto-result-scope-regenerate,
          .InstagramAutoSetupResult .auto-result-hashtag-section
          .auto-result-scope-regenerate {
            top: 0;
            right: 0;
            width: 28px;
            height: 28px;
            box-shadow: 0 3px 10px rgba(26, 55, 72, .1);
          }
          .InstagramAutoSetupResult .auto-result-feed-copy p {
            margin: 0 0 8px;
            white-space: pre-wrap;
          }
          .InstagramAutoSetupResult .auto-result-hashtags {
            margin-bottom: 0;
            color: #5268bf;
            font-weight: 650;
          }
          @media (hover: none) {
            .InstagramAutoSetupResult .auto-result-scope-regenerate {
              opacity: .72;
            }
          }
          .InstagramAutoSetupResult .auto-result-cta {
            margin-top: 9px;
            padding-top: 9px;
            border-top: 1px solid #edf2f4;
            color: #6c7f8a;
            font-weight: 650;
          }
          .InstagramAutoSetupResult .auto-result-bottom-actions {
            display: block;
            margin-top: auto;
          }
          .InstagramAutoSetupResult .auto-result-bottom-button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            width: 100%;
            min-width: 184px;
            min-height: 54px;
            padding: 0 24px;
            border-radius: 12px;
            font-size: 14px;
            font-weight: 800;
            transition:
              transform 0.18s ease,
              box-shadow 0.18s ease,
              opacity 0.18s ease;
          }
          .InstagramAutoSetupResult .auto-result-bottom-button.primary {
            border: 0;
            color: white;
            background: linear-gradient(
              135deg,
              #4f46e5 0%,
              #5b5ee8 55%,
              #0891b2 140%
            );
            box-shadow: 0 8px 20px rgba(79, 70, 229, 0.18);
          }
          .InstagramAutoSetupResult
            .auto-result-bottom-button.primary:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 11px 24px rgba(79, 70, 229, 0.23);
          }
          .InstagramAutoSetupResult
            .auto-result-bottom-button.primary:active:not(:disabled) {
            transform: translateY(0);
          }
          .InstagramAutoSetupResult .auto-result-bottom-button.primary:disabled {
            opacity: 0.64;
            cursor: wait;
            box-shadow: none;
          }
          .InstagramAutoSetupResult .auto-result-fixed-action {
            position: sticky;
            bottom: 0;
            z-index: 30;
            margin: 30px -28px 0;
            padding: 14px 28px;
            border-top: 1px solid #dcecf0;
            background: rgba(255, 255, 255, .96);
            box-shadow: 0 -10px 26px rgba(30, 67, 82, .08);
            backdrop-filter: blur(10px);
          }
          .InstagramAutoSetupResult .auto-result-fixed-action-inner {
            width: 100%;
            max-width: 1120px;
            margin: 0 auto;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 24px;
          }
          .InstagramAutoSetupResult .auto-result-fixed-question {
            display: flex;
            align-items: center;
            gap: 12px;
            color: var(--result-navy);
            font-size: 15px;
            font-weight: 800;
          }
          .InstagramAutoSetupResult .auto-result-fixed-question::before {
            content: '';
            width: 28px;
            height: 28px;
            flex: 0 0 28px;
            border-radius: 7px;
            background: #e9f9fc;
            background-image: url('data:image/svg+xml,<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M22.2655 9.77814C22.2422 9.6733 22.1966 9.57468 22.1319 9.48896C22.0672 9.40324 21.9849 9.33241 21.8905 9.28126L19.6592 8.04376C19.5396 7.80123 19.405 7.5664 19.2561 7.34064L19.303 4.79064C19.3038 4.68368 19.2826 4.5777 19.2407 4.4793C19.1987 4.38091 19.137 4.29221 19.0592 4.21876C17.9482 3.21148 16.6344 2.45386 15.2061 1.99689C15.1035 1.96491 14.9953 1.95505 14.8887 1.96798C14.782 1.98091 14.6793 2.01633 14.5873 2.07189L12.403 3.38439C12.1311 3.37501 11.8686 3.37501 11.5967 3.38439L9.41233 2.07189C9.32037 2.01633 9.21766 1.98091 9.111 1.96798C9.00434 1.95505 8.89615 1.96491 8.79358 1.99689C7.36339 2.45421 6.04898 3.21535 4.94045 4.22814C4.86202 4.29906 4.79969 4.38596 4.75765 4.48298C4.7156 4.58001 4.69482 4.68491 4.6967 4.79064L4.74358 7.34064C4.59358 7.56564 4.46233 7.80001 4.33108 8.04376L2.09983 9.28126C2.00675 9.33294 1.92591 9.40408 1.86282 9.48982C1.79972 9.57557 1.75585 9.67391 1.7342 9.77814C1.41542 11.2422 1.41542 12.7578 1.7342 14.2219C1.75749 14.3267 1.80302 14.4253 1.86772 14.5111C1.93241 14.5968 2.01477 14.6676 2.1092 14.7188L4.34045 15.9563C4.46006 16.1988 4.5947 16.4336 4.74358 16.6594L4.6967 19.2094C4.69584 19.3163 4.71705 19.4223 4.75899 19.5207C4.80093 19.6191 4.8627 19.7078 4.94045 19.7813C6.05142 20.7885 7.36527 21.5462 8.79358 22.0031C8.89615 22.0351 9.00434 22.045 9.111 22.032C9.21766 22.0191 9.32037 21.9837 9.41233 21.9281L11.5967 20.6156H12.403L14.5967 21.9281C14.7107 22.0031 14.8446 22.0423 14.9811 22.0406C15.0573 22.0373 15.1329 22.0248 15.2061 22.0031C16.6363 21.5458 17.9507 20.7847 19.0592 19.7719C19.1376 19.701 19.2 19.6141 19.242 19.517C19.2841 19.42 19.3048 19.3151 19.303 19.2094L19.2561 16.6594C19.4061 16.4344 19.5373 16.2 19.6686 15.9563L21.8998 14.7188C21.9929 14.6671 22.0737 14.596 22.1368 14.5102C22.1999 14.4245 22.2438 14.3261 22.2655 14.2219C22.5842 12.7578 22.5842 11.2422 22.2655 9.77814ZM20.8592 13.575L18.7311 14.7563C18.5961 14.8347 18.4882 14.9525 18.4217 15.0938C18.2675 15.4133 18.0888 15.7205 17.8873 16.0125C17.8017 16.1435 17.7561 16.2966 17.7561 16.4531L17.7936 18.8813C16.9864 19.5685 16.0624 20.1052 15.0655 20.4656L12.9748 19.2094C12.8581 19.1415 12.7255 19.1059 12.5905 19.1063H12.5342C12.1785 19.1344 11.8211 19.1344 11.4655 19.1063C11.3117 19.0979 11.1588 19.1337 11.0248 19.2094L8.9342 20.4656C7.93847 20.1069 7.01464 19.5736 6.20608 18.8906L6.24358 16.4531C6.24352 16.2966 6.19791 16.1435 6.11233 16.0125C5.91428 15.7183 5.73577 15.4114 5.57795 15.0938C5.50873 14.9543 5.40148 14.8373 5.26858 14.7563L3.14045 13.575C2.95289 12.5334 2.95289 11.4666 3.14045 10.425L5.26858 9.24376C5.40359 9.1653 5.5115 9.04757 5.57795 8.90626C5.73219 8.58673 5.91084 8.27957 6.11233 7.98751C6.19791 7.85649 6.24352 7.70339 6.24358 7.54689L6.20608 5.11876C7.01322 4.43151 7.93727 3.89486 8.9342 3.53439L11.0248 4.79064C11.1584 4.86738 11.3117 4.90324 11.4655 4.89376C11.8211 4.86563 12.1785 4.86563 12.5342 4.89376C12.6879 4.90216 12.8408 4.86637 12.9748 4.79064L15.0655 3.53439C16.0612 3.89312 16.985 4.42646 17.7936 5.10939L17.7561 7.54689C17.7561 7.70339 17.8017 7.85649 17.8873 7.98751C18.0854 8.28173 18.2639 8.58864 18.4217 8.90626C18.4909 9.0457 18.5982 9.1627 18.7311 9.24376L20.8592 10.425C21.0468 11.4666 21.0468 12.5334 20.8592 13.575ZM11.9998 6.75001C10.9615 6.75001 9.94644 7.05792 9.08308 7.6348C8.21973 8.21168 7.54682 9.03161 7.14946 9.99093C6.7521 10.9502 6.64813 12.0058 6.8507 13.0242C7.05328 14.0426 7.55329 14.9781 8.28752 15.7123C9.02174 16.4466 9.9572 16.9466 10.9756 17.1491C11.994 17.3517 13.0496 17.2477 14.0089 16.8504C14.9682 16.453 15.7882 15.7801 16.365 14.9168C16.9419 14.0534 17.2498 13.0384 17.2498 12C17.2498 10.6076 16.6967 9.27227 15.7121 8.2877C14.7276 7.30314 13.3922 6.75001 11.9998 6.75001ZM11.9998 15.75C11.2581 15.75 10.5331 15.5301 9.91644 15.118C9.29975 14.706 8.81911 14.1203 8.53528 13.4351C8.25145 12.7499 8.17719 11.9959 8.32188 11.2684C8.46658 10.541 8.82373 9.87281 9.34818 9.34836C9.87262 8.82392 10.5408 8.46676 11.2682 8.32207C11.9957 8.17737 12.7497 8.25164 13.4349 8.53547C14.1201 8.81929 14.7058 9.29994 15.1178 9.91663C15.5299 10.5333 15.7498 11.2583 15.7498 12C15.7498 12.9946 15.3547 13.9484 14.6515 14.6517C13.9482 15.3549 12.9944 15.75 11.9998 15.75Z" fill="%23333333"/></svg>');            
            background-repeat: no-repeat;
            background-position: center;
            background-size: 18px 18px;
          }
          .InstagramAutoSetupResult .auto-result-automation-button {
            height: 42px;
            padding: 0 20px;
            border: 0;
            border-radius: 8px;
            background: #00e2e6;
            color: #0a1a2f;
            font-size: 14px;
            font-weight: 700;
            cursor: pointer;
            transition: 0.2s;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            white-space: nowrap;
            box-shadow: 0 2px 8px rgba(0, 226, 230, 0.2);
          }
          /* Starter 마케팅 분석 팝업의 PDF 다운로드 버튼과 같은 색상 */
          .InstagramAutoSetupResult .auto-result-content-button {
            height: 42px;
            padding: 0 20px;
            border: 0;
            border-radius: 8px;
            background: #F4F5F7;
            color: #333333;
            font-size: 14px;
            font-weight: 700;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            white-space: nowrap;
            transition: 0.2s;
          }
          .InstagramAutoSetupResult .auto-result-content-button:hover:not(:disabled) {
            background: #e7e9ed;
            transform: translateY(-1px);
          }
          .InstagramAutoSetupResult .auto-result-content-button:disabled {
            opacity: .55;
            cursor: not-allowed;
            box-shadow: none;
          }
          .InstagramAutoSetupResult .auto-result-fixed-action-buttons {
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .InstagramAutoSetupResult .auto-result-automation-button:hover:not(:disabled) {
            background: #00bfc2;
            color: #0a1a2f;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 226, 230, 0.3);
          }
          .InstagramAutoSetupResult .auto-result-automation-button:active:not(:disabled),
          .InstagramAutoSetupResult .auto-result-automation-button:focus:not(:disabled) {
            background: #00bfc2 !important;
            color: #0a1a2f !important;
            transform: translateY(-1px) !important;
            box-shadow: 0 4px 12px rgba(0, 226, 230, 0.3) !important;
          }
          .InstagramAutoSetupResult .auto-result-automation-button:disabled {
            opacity: .55;
            cursor: not-allowed;
            box-shadow: none;
          }
          .InstagramAutoSetupResult.empty {
            padding: 50px;
            text-align: center;
            color: #687b87;
          }
          @media (max-width: 900px) {
            .InstagramAutoSetupResult { padding: 22px 16px 0; }
            .InstagramAutoSetupResult .auto-result-grid { grid-template-columns: 1fr; }
            .InstagramAutoSetupResult .auto-result-fixed-action {
              margin-right: -16px;
              margin-left: -16px;
              padding-right: 16px;
              padding-left: 16px;
            }
          }
          @media (max-width: 800px) {
            .InstagramAutoSetupResult {
              --mobile-footer-height: 72px;
              padding-bottom: var(--mobile-footer-height);
            }

            .InstagramAutoSetupResult .auto-result-fixed-action {
              bottom: var(--mobile-footer-height);
            }
          }
          @media (max-width: 650px) {
            .InstagramAutoSetupResult .auto-result-banner {
              align-items: stretch;
              flex-direction: column;
            }
            .InstagramAutoSetupResult .auto-result-banner-main { align-items: flex-start; }
            .InstagramAutoSetupResult .auto-result-outline-button { width: 100%; }
            .InstagramAutoSetupResult .auto-result-field-grid,
            .InstagramAutoSetupResult .auto-result-field-grid.cta {
              grid-template-columns: 1fr;
            }
            .InstagramAutoSetupResult .auto-result-success-toast {
              top: calc(var(--header-height) + 12px);
              min-width: 0;
              width: calc(100vw - 24px);
              padding: 13px 16px;
              border-radius: 10px;
              font-size: 14px;
            }
            .InstagramAutoSetupResult .auto-result-fixed-action-inner {
              align-items: stretch;
              flex-direction: column;
              gap: 12px;
            }
            .InstagramAutoSetupResult .auto-result-fixed-action-buttons { width: 100%; }
            .InstagramAutoSetupResult .auto-result-content-button,
            .InstagramAutoSetupResult .auto-result-automation-button { flex: 1; width: 100%; padding: 0 10px; }
          }

          .auto-result-animate-spin {
            width: 24px;
            height: 24px;
            animation: autoResultSpin 1s linear infinite;
          }

          @keyframes autoResultSpin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>

        {this.state.showSuccessToast && (
          <div
            className="auto-result-success-toast"
            role="status"
            aria-live="polite"
          >
            피드 생성과 자동화 세팅을 완료했습니다.
          </div>
        )}

        <div className="auto-result-wrap">
          <div className="auto-result-page-header">
            <h1 className="auto-result-page-title">
              AI 자동화 마법사
            </h1>
            <div className="auto-result-header-actions">
              <button
                type="button"
                className="auto-result-return-button"
                onClick={this.handleReturnToStarter}
                disabled={this.state.isSaving || this.state.isManualSaving}
              >
                ← 처음으로 돌아가기
              </button>
              <button
                type="button"
                className="auto-result-manual-save-button"
                onClick={this.handleManualSave}
                disabled={
                  this.state.isManualSaving ||
                  this.state.isSaving ||
                  this.state.isRegenerating ||
                  this.state.isImageGenerating
                }
              >
                {this.state.isManualSaving
                  ? "저장 중..."
                  : this.state.manualSaveComplete
                  ? "저장 완료"
                  : "저장"}
              </button>
            </div>
          </div>

          {/* <div className="auto-result-status-line">
            <button
              type="button"
              className="auto-result-status-button"
            >
              자동화 활성화 완료
            </button>
          </div> */}

          <section className="auto-result-banner">
            <div className="auto-result-banner-main">
              <div className="auto-result-banner-icon" aria-hidden="true">
                <svg
                  className="auto-result-animate-spin"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </div>
              <div>
                <div className="auto-result-title-row">
                  <h2 className="font-bold">
                    GPT API 분석을 통한 카피라이터 설정 및 AI 세팅이 완료되었습니다!
                  </h2>
                  <span className="auto-result-mapped">AI MAPPED</span>
                </div>
                <p>
                  업로드하신 사업계획서/링크를 분석하여 맞춤형 타겟 설정과 AI 스튜디오 미리보기를 구성했습니다.
                </p>
              </div>
            </div>

            <button
              type="button"
              className={`auto-result-outline-button ${
                this.state.isEditing ? "active" : ""
              }`}
              onClick={this.toggleEdit}
              disabled={false}
              title={false
                ? "이미 저장한 카피라이터 설정을 재사용하므로 추가 수정은 할 수 없습니다."
                : undefined}
            >
              설정값 직접 수정
            </button>
          </section>

          <div className="auto-result-grid">
            <section className="auto-result-card auto-result-settings">
              <div className="auto-result-card-heading">
                <h3 className="font-bold">
                  {/* 📋 GPT 자동 매핑된 카피라이터 설정 */}
                  AI카피라이터 설정 
                </h3>
                {/* <span className="auto-result-setting-badge">
                  설정 제목: {copywriter.preset_title || "-"}
                </span> */}
              </div>

              <div className="auto-result-fields">
                {this.renderField({
                  label: "업종 특성",
                  value: sourceAnalysis.industry,
                })}

                <div className="auto-result-field-grid">
                  {this.renderField({
                    label: "주요 타겟 고객",
                    value: copywriter.target,
                    section: "copywriter",
                    fieldKey: "target",
                  })}
                  {this.renderField({
                    label: "생성 언어",
                    value: this.getLanguageLabel(
                      copywriter.language_code
                    ),
                  })}
                </div>

                {this.renderField({
                  label: "생성 목적",
                  value: copywriter.objective,
                  section: "copywriter",
                  fieldKey: "objective",
                })}

                {this.renderField({
                  label:
                    "AI 추가 명령 프롬프트",
                  value: copywriter.extra_prompt,
                  section: "copywriter",
                  fieldKey: "extra_prompt",
                  multiline: true,
                })}

                <div className="auto-result-field-grid cta">
                  {this.renderField({
                    label: "첨부 위치 선택",
                    value: this.getCtaPositionLabel(
                      copywriter.call_to_action_position
                    ),
                  })}
                  {this.renderField({
                    label: "고정 행동 유도 문구",
                    value: copywriter.call_to_action_text,
                    section: "copywriter",
                    fieldKey: "call_to_action_text",
                  })}
                </div>
              </div>
            </section>

            <section className="auto-result-card auto-result-preview-card">
              <div>
                <div className="auto-result-card-heading">
                  <div>
                    <h3 className="font-bold">
                      {/* 🎨 AI 스튜디오 추천 이미지 연동 */}
                      AI 자동화 결과
                    </h3>
                    <p className="auto-result-preview-desc">
                      분석 결과로 생성한 AI 이미지와 실시간 피드 시안입니다.
                    </p>
                  </div>
                  {/* <span className="auto-result-template-badge">
                    템플릿: {templateName}
                  </span> */}
                  <div className="auto-result-bottom-actions">
                        <button
                        type="button"
                        className="auto-result-bottom-button primary"
                        onClick={this.handleRemake}
                        disabled={
                            this.state.isRegenerating ||
                            this.state.isImageGenerating ||
                            this.state.isSaving ||
                            this.state.isManualSaving
                        }
                        >
                        {this.state.isRegenerating
                            ? "다시 만드는 중..."
                            : this.state.isImageGenerating
                            ? this.state.regeneratingTarget === "IMAGE"
                            ? "이미지만 다시 만드는 중..."
                            : "이미지 생성 중..."
                            : "AI 결과 다시 만들기"}
                        </button>
                    </div>
                </div>

                <div className="auto-result-feed">
                  <div className="auto-result-feed-head">
                    <div className="auto-result-account">
                      <div className="auto-result-avatar">
                        {avatarText}
                      </div>
                      <div>
                        <strong>{displayName}</strong>
                        <small>AI 자동화 Preview</small>
                      </div>
                    </div>
                    <span className="auto-result-feed-more">•••</span>
                  </div>

                  <div className="auto-result-image-wrap auto-result-regenerate-scope">
                    {this.renderImageArea(previewImage)}
                    {this.renderScopeRegenerateButton(
                      "IMAGE",
                      "이미지만 다시 만들기"
                    )}
                  </div>

                  <div className="auto-result-feed-copy">
                    <div className="auto-result-caption-section auto-result-regenerate-scope">
                      {this.renderScopeRegenerateButton(
                        "CAPTION",
                        "문구만 다시 만들기"
                      )}
                      <div className="auto-result-caption-text">
                        <strong>{displayName}</strong>{" "}
                        {content.preview_caption || ""}
                      </div>
                    </div>
                    {copywriter.call_to_action_text && (
                      // <div className="auto-result-cta">
                      <p>
                        {copywriter.call_to_action_text}
                      </p>
                    )}
                    <div className="auto-result-hashtag-section auto-result-regenerate-scope">
                      {this.renderScopeRegenerateButton(
                        "HASHTAGS",
                        "해시태그만 다시 만들기"
                      )}
                      <p className="auto-result-hashtags">
                        {hashtags}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className="auto-result-fixed-action">
            <div className="auto-result-fixed-action-inner">
              <div className="auto-result-fixed-question">
                {"현재 설정으로 이번 한번만 콘텐츠를 발행하거나, 같은 형식으로 자동 발행하시겠습니까?"}
              </div>
              <div className="auto-result-fixed-action-buttons">
                {(
                  <button
                    type="button"
                    className="auto-result-content-button"
                    onClick={this.handleContentRegistration}
                    disabled={
                      this.state.isRegenerating ||
                      this.state.isImageGenerating ||
                      this.state.isSaving ||
                      this.state.isManualSaving
                    }
                  >
                    {this.state.isSaving && this.state.contentRegistrationModalOpen
                      ? "이번만 발행 중..." : "이번만 발행"}
                  </button>
                )}
              <button
                type="button"
                className="auto-result-automation-button"
                onClick={this.handleFinalSave}
                disabled={
                  this.state.isRegenerating ||
                  this.state.isImageGenerating ||
                  this.state.isSaving ||
                  this.state.isManualSaving
                }
              >
                {this.state.isSaving && !this.state.contentRegistrationModalOpen
                  ? "자동발행등록 중..." : "자동발행등록"}
              </button>
              </div>
            </div>
          </div>

        {this.state.warningDialogMessage && (
          <WarningDialog
            content={toDialogContent(this.state.warningDialogMessage)}
            button_list={[{
              title: "확인",
              event_method: this.closeWarningDialog,
            }]}
            popup_close={this.closeWarningDialog}
          />
        )}

        {this.state.confirmReturnToStarterOpen && (
          <ConfirmDialogV2
            content="처음으로 돌아가시겠습니까?"
            button_list={[
              {
                title: "확인",
                type: "1",
                event_method: this.confirmReturnToStarter,
              },
              {
                title: "취소",
                type: "2",
                event_method: () => this.setState({ confirmReturnToStarterOpen: false }),
              },
            ]}
            popup_close={() => this.setState({ confirmReturnToStarterOpen: false })}
          />
        )}

        <InstagramAutoSetupSaveProgressModal
          isOpen={this.state.saveProgressModalOpen}
          status={this.state.saveProgressStatus}
          currentStepNo={this.state.saveProgressCurrentStepNo}
          completedStepNo={this.state.saveProgressCompletedStepNo}
          message={this.state.saveProgressMessage}
          errorMessage={this.state.saveProgressError}
          result={this.state.saveProgressResult}
          reuseCopywriter={false}
          activeWeekdays={automation.active_weekdays}
          profileUpdateMode={false}
          onRetry={() => this.handleFinalSave("RETRY")}
          onClose={this.closeSaveProgressModal}
          onOpenCompanyRepository={this.openCompanyRepository}
          onOpenCopywriterSetting={this.openCopywriterSetting}
          onOpenAutomationSetting={this.openAutomationSetting}
        />

        <InstagramAutoSetupSaveProgressModal
          isOpen={this.state.contentRegistrationModalOpen}
          contentRegistrationMode={true}
          status={this.state.contentRegistrationStatus}
          currentStepNo={this.state.contentRegistrationCurrentStepNo}
          completedStepNo={this.state.contentRegistrationCompletedStepNo}
          message={this.state.contentRegistrationMessage}
          errorMessage={this.state.contentRegistrationError}
          result={this.state.contentRegistrationResult}
          reuseCopywriter={false}
          onRetry={() => this.handleContentRegistration("RETRY")}
          onClose={this.closeContentRegistrationModal}
          onOpenCopywriterSetting={this.openRegisteredCopywriter}
          onOpenDraftContent={this.openRegisteredContent}
        />
      </div>
    );
  }
}

export default InstagramAutoSetupResult;